import type { KyInstance } from 'ky';

import { ensureError, formatString, isNotDefined } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import type { MailingSettings } from '#mailing/types';

import { Uri } from '#constants/uri';

/**
 * Fetches the mailing settings for a given mailing ID.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param mailingId - The unique identifier of the mailing whose settings are to be fetched.
 * @returns A promise that resolves to an {@link OperationResultWithData} containing the {@link MailingSettings}.
 * @throws {Error} If the mailingId is not defined or if the request fails.
 */
export const getMailingSettings = async (instance: KyInstance, mailingId?: number) => {
  try {
    if (isNotDefined(mailingId)) {
      throw new Error('Mailing ID is required to fetch mailing settings.');
    }

    return await instance
      .get<OperationResultWithData<MailingSettings>>(formatString(Uri.mailing.getMailingSettings, mailingId.toString()))
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
